import toast from 'react-hot-toast'
import copyToClipboard from 'clipboard-copy'
import defaults from 'lodash/defaults'

import { Logger } from '../Logger'

const log = Logger('ErrorHelper')

export const timeoutCode = 'ECONNABORTED'

/**
 *  Search through the array of error messages and look for one with a
 *  matching value.
 *
 * @param error - an error object
 * @param messages - an array of error messages to search for
 * @param options - an object of options (strict for now)
 * @returns {boolean} true if found, false otherwise
 */
export const includesOneOfErrorMessages = (error, messages, options = { strict: false }) => {
  if (!error) return false
  if (!messages || messages.length === 0) return false

  const messageCmp = (msg1, msg2, strict) => (!strict ? msg1?.toLowerCase()?.includes(msg2?.toLowerCase()) : msg1 === msg2)

  const data = error.response?.data
  if (Array.isArray(data?.errors)) {
    return messages.some((message) => {
      const check = (m) => messageCmp(m, message, options?.strict)

      return data.errors.some(({ detail }) => {
        return Array.isArray(detail) ? detail.some(check) : check(detail)
      })
    })
  }
  // Some endpoints return the message under `error`, others under `info` (e.g. enrollment
  // "Email taken by enrolled user.") — check both so callers don't have to know the shape.
  return messages.some((message) => [data?.error, data?.info].some((value) => messageCmp(value, message, options?.strict)))
}

export const getErrorCode = (error) => {
  if (!error) return undefined
  if (error.response?.status === 403 && includesOneOfErrorMessages(error, ['does not has permissions on this endpoint'])) {
    return 401
  }
  return error.response?.status === 404 ? 404 : 500
}

export const getErrorDetails = (error) => error?.response?.data?.errors?.map((e) => e?.detail) || []

export function isDynamicLoadError(error) {
  if (!error || !error.name) return false

  if (error.name === 'ChunkLoadError') return true
  if (error.name === 'TypeError' && error.message?.includes('Failed to fetch dynamically imported module')) return true
  // Firefox / Safari wording of the same failure
  if (error.name === 'TypeError' && error.message?.includes('error loading dynamically imported module')) return true
  // Stale chunk served with an empty/wrong MIME type (Safari)
  if (error.name === 'TypeError' && error.message?.includes('is not a valid JavaScript MIME type')) return true
  // Vite CSS preload failure for a lazy chunk
  if (error.message?.includes('Unable to preload CSS')) return true
  // Happens for Safari < 17
  if (error.name === 'TypeError' && error.message?.includes('Load failed')) return true
  // Happens for Safari >= 17
  if (error.name === 'TypeError' && error.message?.includes('Importing a module script failed.')) return true

  return false
}

// Errors thrown by in-app browser injected scripts (e.g. Facebook/Instagram `autofill_contact_enhanced`,
// or Firefox-iOS's `window.__firefox__` helpers). The backtrace originates entirely from the injected
// script and never touches our code; it only reaches Honeybadger because it runs in the same global
// scope and is caught by window.onerror.
export function isInAppBrowserError(error) {
  if (!error) return false
  const stack = typeof error.stack === 'string' ? error.stack : JSON.stringify(error.backtrace ?? '')
  if (stack.includes('iabjs://')) return true
  return Boolean(error.message?.includes('__firefox__'))
}

// Errors originating from third-party scripts and browser extensions injected into the page — not our
// code, not actionable. Matched by the distinctive global each one references.
const THIRD_PARTY_ERROR_SIGNATURES = [
  'refresh_youtube_quality', // Firefox-iOS injected helper
  'GTM_VERSION', // Google Tag Manager double-load
  'window.ethereum', // crypto-wallet browser extension writing to the page's global scope
  'runtime.sendMessage', // browser-extension messaging API ("...Tab not found."), never our code
  "Can't find variable: BroadcastChannel", // unsupported/locked-down browsers
  "Can't find variable: WebSocket",
  'MethodName:simulateEvent', // Outlook/Bing SafeLinks crawler probing the page before a user opens it
  'document.elementFromPoint', // same crawler — our code never calls it, so any such error is injected
]

// Browser extensions execute in the page's own global scope, so their unhandled rejections reach
// window.onerror as if they were ours. Their frames always carry the extension URL scheme; our bundle
// never does. Matched by scheme rather than by extension id so new extensions don't need a new entry.
const EXTENSION_SCHEMES = ['chrome-extension://', 'moz-extension://', 'safari-web-extension://']

// Injected third-party scripts whose backtrace frames live on these hosts. Their errors only reach us
// because they run in the same global scope (e.g. Google Translate's element.js mutating the DOM after
// React has moved on). The thrown message is minified/unstable, so match on the script origin instead.
const THIRD_PARTY_SCRIPT_HOSTS = ['translate.google.com', 'translate.googleapis.com']

export function isThirdPartyError(error) {
  if (!error) return false
  if (error.message && THIRD_PARTY_ERROR_SIGNATURES.some((signature) => error.message.includes(signature))) return true
  const stack = typeof error.stack === 'string' ? error.stack : JSON.stringify(error.backtrace ?? '')
  if (EXTENSION_SCHEMES.some((scheme) => stack.includes(scheme))) return true
  return THIRD_PARTY_SCRIPT_HOSTS.some((host) => stack.includes(host))
}

// Uncaught Axios client errors (4xx) re-surface as generic unhandled-rejection notices
// ("Request failed with status code 4xx") carrying no endpoint context — a low-value duplicate of the
// richer notice `reportAxiosError` already files in the interceptor (and filters via its allowlist).
// Drop the 4xx duplicates here; 5xx ("...status code 5xx") still report, so real server errors and any
// unexpected client error (which still files its rich, allowlist-checked copy) remain visible.
export function isAxiosClientErrorRejection(error) {
  return /Request failed with status code 4\d\d/.test(error?.message ?? '')
}

// getUserMedia failures raised during a video visit when the patient/provider denies camera or mic
// access, or the device is already in use / missing. These are DOMExceptions thrown by the browser's
// media stack (NotAllowedError / NotReadableError / InvalidStateError) — a user-environment condition the
// UI already handles, not a frontend defect. Match on the message because InvalidStateError is also used
// for unrelated APIs (e.g. BroadcastChannel.postMessage), which must stay visible.
const MEDIA_DEVICE_ERROR_SIGNATURES = [
  'Could not start video source', // camera busy / unavailable
  'Failed to start the audio device', // microphone busy / unavailable
  'The request is not allowed by the user agent', // user denied camera/mic permission
]

export function isMediaDeviceError(error) {
  if (!error?.message) return false
  // Chrome's wording when the patient dismisses or blocks the camera/mic prompt. Gated on the
  // NotAllowedError name because "Permission denied" on its own is far too generic to match safely.
  if (error.name === 'NotAllowedError' && error.message.includes('Permission denied')) return true
  return MEDIA_DEVICE_ERROR_SIGNATURES.some((signature) => error.message.includes(signature))
}

// Benign, well-known browser noise: the ResizeObserver loop notification is fired by the browser when
// observations can't be delivered in one frame; it has no effect and isn't a defect.
// https://stackoverflow.com/q/49384120
export function isResizeObserverError(error) {
  return Boolean(error?.message?.includes('ResizeObserver loop'))
}

// Deliberate cancellation of an in-flight request, from either layer: query-core throws `CancelledError`
// when clear()/cancelQueries destroys a running query (e.g. on logout), and axios throws `CanceledError`
// when an AbortController signal fires (e.g. a superseded request being re-issued). Note the spelling
// difference — one L for axios, two for query-core. Both are intentional, not defects. The reason can
// arrive bare or wrapped by the unhandledrejection handler ("UnhandledPromiseRejectionWarning: ..."), so
// match on substring rather than equality.
export function isCancelledRequestError(error) {
  if (typeof error?.message !== 'string') return false
  return error.message.includes('CancelledError') || error.message.includes('CanceledError')
}

// Related to GoogleTranslate DOM mutation https://github.com/facebook/react/issues/11538
export function isDOMMutationError(error) {
  if (!error || !error.name) return false
  if (error.name === 'NotFoundError' && error.message?.includes("'removeChild' on 'Node'")) return true
  if (error.name === 'NotFoundError' && error.message?.includes("'insertBefore' on 'Node'")) return true

  return false
}

// Unhandled promise rejections whose reason carries no actionable payload. Honeybadger's
// onunhandledrejection handler reports any non-Error reason as `window.onunhandledrejection` with the
// message `UnhandledPromiseRejectionWarning: ${JSON.stringify(reason)}`. When the reason is a non-Error
// object with no enumerable properties (e.g. a DOMException/AbortError) or an empty object rejected
// without a `.catch`, that serialises to `{}` (or `[]`/`null`) — leaving no stack or message to attribute
// it to our code. Safari emits these in volume for a handful of users; treat as noise.
const CONTENTLESS_REJECTION_PAYLOADS = ['{}', '[]', 'null', 'Unspecified reason']

export function isContentlessRejection(error) {
  if (!error || error.name !== 'window.onunhandledrejection') return false
  const payload = error.message?.replace('UnhandledPromiseRejectionWarning: ', '').trim()
  return CONTENTLESS_REJECTION_PAYLOADS.includes(payload)
}

export function handleError(error, options) {
  const { message, showResponse } = defaults(options, {
    message: 'There was an issue. Please contact customer support or try again.',
    showResponse: false,
  })

  const detailedMessage = formatErrorToString(error)
  log.warn(detailedMessage)

  let finalMessage = message

  if (showResponse) {
    finalMessage = getErrorDetails(error).join('\n') || message
  }

  toast.error(<CopyErrorMessage error={detailedMessage}>{finalMessage}</CopyErrorMessage>)
}

export function handleErrorSilently(error) {
  log.warn(formatErrorToString(error))
}

const errorStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '5px',
  },
  text: {
    cursor: 'pointer',
    fontSize: '0.7rem',
    textAlign: 'right',
  },
}

function CopyErrorMessage({ error, children }) {
  return (
    <div style={errorStyles.container}>
      <span>{children}</span>
      <span style={errorStyles.text} title="Copy error detail to clipboard" onClick={() => copyToClipboard(error)}>
        Copy
      </span>
    </div>
  )
}

const isHtmlContent = (data, contentType) => {
  return (
    typeof data === 'string' &&
    (contentType?.includes('text/html') || data.trim().startsWith('<!DOCTYPE') || data.trim().startsWith('<html'))
  )
}

const getErrorStack = (error) => {
  return error.stack.substring(0, 300) + (error.stack.length > 300 ? '...' : '')
}

const formatAxiosError = (error) => {
  const responseData = error.response?.data
  const contentType = error.response?.headers?.['content-type']
  const isHtml = isHtmlContent(responseData, contentType)

  return {
    object: {
      type: 'Axios Error',
      url: [error.config?.method?.toUpperCase(), error.config?.url].filter(Boolean).join(' '),
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
      ...(error.stack && { stack: getErrorStack(error) }),
      ...(!isHtml && { responseData }),
    },
    html: isHtml ? responseData : undefined,
  }
}

const formatJsError = (error) => {
  const object = {
    type: 'JavaScript Error',
    name: error.name,
    message: error.message,
    ...(error.stack && { stack: getErrorStack(error) }),
  }

  return { object }
}

const formatGenericError = (error) => {
  const object = typeof error === 'object' ? { type: 'Object Error', ...error } : { type: 'Generic Error', value: String(error) }
  return { object }
}

const safeStringify = (obj) => {
  const seen = new WeakSet()
  return JSON.stringify(
    obj,
    (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) return '[Circular]'
        seen.add(value)
      }
      return value
    },
    2
  )
}

export const formatError = (error) => {
  if (!error) return { text: 'No error information available' }

  let result
  if (error.isAxiosError) {
    result = formatAxiosError(error)
  } else if (error instanceof Error) {
    result = formatJsError(error)
  } else {
    result = formatGenericError(error)
  }

  // Add context information
  const enhancedError = {
    ...result.object,
    context: {
      id: window?.user?.id,
      fullName: window?.user?.fullName,
      role: window?.user?.role,
      currentUrl: window.location.href,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    },
  }

  return {
    text: safeStringify(enhancedError),
    html: result.html,
  }
}

export const format = (text, html) => (html ? `${text}\n\n--- HTML Response ---\n${html}` : text)

export const formatErrorToString = (error) => {
  const { text, html } = formatError(error)
  return format(text, html)
}

export const downloadErrorDetails = (error, filename = 'error-details') => {
  const { text, html } = formatError(error)
  const content = format(text, html)

  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${filename}-${new Date().toISOString()}.txt`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
